import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth, useWallet } from '@crossmint/client-sdk-react-ui'
import { Wallet } from 'lucide-react'
import { useEtherith } from './hooks/useEtherith'
import { usePublicArchiveManager, PublicArchiveProvider } from './store/PublicArchiveManager.jsx'

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 Error Boundary caught an error:', error, errorInfo)
    this.setState({
      error: error,
      errorInfo: errorInfo
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-vault-bg flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <div className="text-6xl mb-4">🚨</div>
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <p className="text-vault-text/70 mb-6">
              The application encountered an unexpected error. Please refresh the page to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-vault-accent text-white px-6 py-3 rounded-lg hover:bg-vault-accent/80 transition-colors"
            >
              Refresh Page
            </button>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm">Error Details (Dev Mode)</summary>
                <pre className="text-xs mt-2 p-4 bg-black/10 rounded overflow-auto">
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Helper function to extract wallet address (same as in useEtherith)
const getWalletAddress = (wallet) => {
  if (!wallet) return null

  const possibleAddressProps = [
    'address', 'walletAddress', 'publicKey', 'account', 'userAddress',
    'smartWallet.address', 'wallet.address', 'account.address'
  ]

  for (const prop of possibleAddressProps) {
    if (prop.includes('.')) {
      const parts = prop.split('.')
      let value = wallet
      for (const part of parts) {
        value = value?.[part]
        if (!value) break
      }
      if (value && typeof value === 'string' && value.startsWith('0x')) {
        return value
      }
    } else {
      const value = wallet[prop]
      if (value && typeof value === 'string' && value.startsWith('0x')) {
        return value
      }
    }
  }
  return null
}
import { ResponsiveProvider, useResponsiveContext } from './context/ResponsiveContext'
import AdaptiveNavigation from './components/AdaptiveNavigation'
import VaultView from './components/VaultView'
import UploadView from './components/UploadView'
import ArchiveView from './components/ArchiveView'
import ProfileView from './components/ProfileView'
import SharedDocumentManager from './components/SharedDocumentManager'
import SettingsView from './components/SettingsView'
import DebugPanel from './components/DebugPanel'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import WalletFAB from './components/WalletFAB'
import WalletOnboarding from './components/WalletOnboarding'
import { WalletConnect } from './components/WalletConnect'
import { UIShowcase } from './components/enhanced'
import { commonPatterns } from './utils/mobileFirst'
import ViewportDebugger from './components/ViewportDebugger'

const views = {
  vault: { title: 'Vault', subtitle: 'Preserve what matters', component: VaultView },
  upload: { title: 'Preserve', subtitle: 'Every memory, proofed forever', component: UploadView },
  archive: { title: 'Archive', subtitle: 'Safe. Private. Immutable.', component: ArchiveView },
  profile: { title: 'Identity', subtitle: 'Your digital signature', component: ProfileView },
  settings: { title: 'Protocol', subtitle: 'Configure preservation', component: SettingsView },
  'shared-doc': { title: 'Document Manager', subtitle: 'Reset shared public archive', component: SharedDocumentManager },
  showcase: { title: 'UI Demo', subtitle: 'Enhanced component system', component: UIShowcase }
}

/**
 * AppContent - Main application content (inside ResponsiveProvider)
 */
function AppContent() {
  const [currentView, setCurrentView] = useState('vault')
  const [isArchiveMode, setIsArchiveMode] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const { showSidebar, showBottomNav, shouldAnimate, isMobile } = useResponsiveContext()

  // Crossmint authentication state
  const { jwt, isAuthenticated: crossmintAuth } = useAuth()
  const { wallet } = useWallet()
  const isAuthenticated = !!(jwt && wallet)
  
  // Log authentication state for debugging
  console.log('🔐 Crossmint Auth State:', {
    jwt: !!jwt,
    jwtValue: jwt,
    wallet: !!wallet,
    walletObject: wallet,
    walletAddress: wallet ? getWalletAddress(wallet) : null,
    isAuthenticated,
    crossmintAuth
  })

  const {
    isInitialized,
    user,
    memories,
    publicMemories,
    connectionStatus,
    syncStatus,
    debugInfo,
    uploadMemory,
    loadMemories,
    loadPublicArchive,
    searchArchive,
    checkSyncStatus,
    forcePeerDiscovery,
    forcePublicArchiveSync,
    peerCount,
    networkStatus,
    isWalletConnected,
    p2pMemories
  } = useEtherith(wallet)

  // Initialize unified public archive manager
  const archiveManager = usePublicArchiveManager()
  
  // Initialize public archive when app starts
  useEffect(() => {
    if (!archiveManager.isReady && isInitialized) {
      console.log('🚀 Initializing unified public archive...')
      archiveManager.initializePublicArchive().catch(console.error)
    }
  }, [archiveManager.isReady, archiveManager.initializePublicArchive, isInitialized])

  // Get current user identifier for filtering
  const currentUserId = useMemo(() => {
    return wallet?.address || user?.id || null
  }, [wallet?.address, user?.id])

  // Combine user's private and public memories for vault view
  const vaultMemories = useMemo(() => {
    const privateMemories = memories || []
    const userPublicMemories = currentUserId ? 
      (publicMemories || []).filter(memory => memory.userId === currentUserId) : []

    // Remove duplicates by checking for existing IDs in private memories
    const privateMemoryIds = new Set(privateMemories.map(m => m.id))
    const uniqueUserPublicMemories = userPublicMemories.filter(memory => 
      !privateMemoryIds.has(memory.id)
    )

    const combined = [...privateMemories, ...uniqueUserPublicMemories]

    console.log('🔄 Vault Memory Combination:', {
      privateMemories: privateMemories.length,
      userPublicMemories: userPublicMemories.length,
      uniqueUserPublicMemories: uniqueUserPublicMemories.length,
      currentUserId,
      totalVaultMemories: combined.length
    })

    return combined.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  }, [memories, publicMemories, currentUserId])

  // 🎯 UNIFIED: Use the single source of truth for public memories
  const combinedPublicMemories = useMemo(() => {
    // Use unified archive manager as primary source
    const unifiedMemories = archiveManager.publicMemories || []
    const fallbackMemories = publicMemories || []
    
    // Use unified if available, fallback to legacy
    const sourceMemories = unifiedMemories.length > 0 ? unifiedMemories : fallbackMemories
    
    const archiveMemories = sourceMemories.map(memory => ({
      ...memory,
      isCollaborative: false, // Regular IPFS memories are not collaborative
      preservationMode: memory.preservationMode || 'public',
      visibility: memory.visibility || 'public'
    }))
    
    // Add P2P collaborative memories
    const collaborativeMemories = (p2pMemories?.activeCollaborations || []).map(collab => ({
      id: collab.memoryId,
      note: collab.title || 'Collaborative Memory',
      type: 'collaborative',
      fileName: collab.title || 'Collaborative Memory',
      fileSize: 0,
      timestamp: collab.lastActivity || Date.now(),
      userId: 'collaborative',
      status: 'collaborative',
      visibility: 'public',
      preservationMode: 'public',
      isCollaborative: true,
      activeUsers: collab.activeUsers || [],
      collaborationId: collab.memoryId
    }))

    // Merge and deduplicate
    const combined = [...archiveMemories]
    collaborativeMemories.forEach(collabMem => {
      if (!combined.find(mem => mem.collaborationId === collabMem.collaborationId)) {
        combined.push(collabMem)
      }
    })

    console.log('📋 UNIFIED Archive Memories:', combined.length, 
      'IPFS memories:', archiveMemories.length,
      'P2P memories:', collaborativeMemories.length
    )

    return combined.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  }, [archiveManager.publicMemories, publicMemories, p2pMemories?.activeCollaborations])

  // Check if onboarding should be shown
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      const hasSeenOnboarding = localStorage.getItem('etherith-onboarding-seen')
      if (!hasSeenOnboarding) {
        // Small delay to let the app initialize
        const timer = setTimeout(() => {
          setShowOnboarding(true)
        }, 1000)
        return () => clearTimeout(timer)
      }
    }
  }, [isInitialized, isAuthenticated])

  // Handle view switching
  const switchView = useCallback((viewName) => {
    setCurrentView(viewName)
    setIsArchiveMode(viewName === 'archive')

    // Load view-specific data
    if (viewName === 'archive') {
      loadPublicArchive()
    } else if (viewName === 'vault') {
      // Ensure both private and public memories are loaded for vault view
      loadMemories()
      loadPublicArchive()
    }
  }, [loadPublicArchive, loadMemories])

  // Listen for custom view switch events
  useEffect(() => {
    const handleViewSwitch = (event) => {
      switchView(event.detail)
    }

    window.addEventListener('switch-view', handleViewSwitch)
    return () => window.removeEventListener('switch-view', handleViewSwitch)
  }, [loadPublicArchive, loadMemories])

  // Get current view data
  const currentViewData = views[currentView] || views.vault
  const ViewComponent = currentViewData.component

  // Enhanced page transition variants
  const pageVariants = {
    initial: { opacity: 0, y: 30, scale: 0.98 },
    in: { opacity: 1, y: 0, scale: 1 },
    out: { opacity: 0, y: -30, scale: 1.02 }
  }

  const pageTransition = {
    type: 'tween',
    ease: [0.33, 1, 0.68, 1], // luxury easing
    duration: 0.8
  }

  // Show loading only if we have a wallet but haven't initialized yet
  if (wallet?.address && !isInitialized) {
    return (
      <div className="min-h-screen-mobile lg:min-h-screen bg-vault-bg flex items-center justify-center p-content">
        <div className="text-center max-w-sm">
          <motion.div
            className="w-20 h-20 border-4 border-current border-t-transparent rounded-full mx-auto mb-8"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-current mb-3">
            Initializing Etherith...
          </h2>
          <p className="text-base sm:text-lg text-current/70 mb-4">
            Loading memory vault systems
          </p>
          <div className="flex justify-center space-x-1">
            <motion.div
              className="w-2 h-2 bg-current rounded-full"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
            />
            <motion.div
              className="w-2 h-2 bg-current rounded-full"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
            />
            <motion.div
              className="w-2 h-2 bg-current rounded-full"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
            />
          </div>
        </div>
      </div>
    )
  }

  // Wallet connection gate - require authentication for all app functionality
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen-mobile lg:min-h-screen bg-vault-bg flex items-center justify-center p-content">
        <div className="text-center max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-vault-accent to-vault-accent/70 rounded-full mx-auto mb-6 flex items-center justify-center">
              <Wallet className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-current mb-4">
              Connect Your Wallet
            </h1>
            <p className="text-lg text-current/70 mb-8 leading-relaxed">
              Connect your wallet to access your personal memory vault and start preserving your most precious moments.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
            className="space-y-4"
          >
            <WalletConnect
              variant="full"
              showStatus={true}
              className="w-full max-w-sm mx-auto"
            />
            
            <div className="text-sm text-current/50 mt-6">
              <p>Your memories are encrypted and stored securely on IPFS</p>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className={`
      min-h-screen-mobile lg:min-h-screen transition-all duration-slow ease-luxury
      ${isArchiveMode ? 'archive-mode' : 'bg-vault-bg text-vault-text'}
      ${showSidebar ? 'lg:flex lg:flex-row' : ''}
    `}>
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      
      {/* Adaptive Navigation System */}
      <AdaptiveNavigation
        currentView={currentView}
        onViewChange={switchView}
        isArchiveMode={isArchiveMode}
        connectionStatus={connectionStatus}
        onSearchPress={() => console.log('Search pressed')}
        onNotificationPress={() => console.log('Notifications pressed')}
        showWalletWidget={true}
      />

      {/* Main Content Layout - Fixed Desktop Layout */}
      <div className={`
        ${showBottomNav ? 'pb-nav-bar' : ''}
        ${showBottomNav ? 'pt-top-bar' : ''}
        ${showSidebar ? 'lg:flex-1 lg:min-h-screen' : ''}
        transition-all duration-500 ease-luxury
        ${!showSidebar ? commonPatterns.responsiveContainer : 'w-full'}
      `}>

        {/* Main Content Area - Fixed Desktop Layout */}
        <main
          id="main-content"
          className={`
            flex flex-col min-h-screen-safe
            ${showSidebar ? 'lg:flex-1 lg:min-h-screen lg:w-full lg:bg-vault-bg' : ''}
            bg-vault-bg transition-all duration-500 ease-luxury
            ${showSidebar ? 'lg:shadow-inner' : ''}
          `}
          role="main"
          aria-label="Main content area"
        >

          {/* Content Container - Fixed Desktop Centering */}
          <div className={`
            flex-1 p-content sm:p-content-sm md:p-content-md lg:p-content-lg xl:p-content-xl
            ${showSidebar ? 'lg:overflow-y-auto lg:h-screen lg:scrollbar-thin lg:w-full' : ''}
            scrollbar-thin relative max-w-full
            ${!showSidebar ? 'lg:max-w-7xl lg:mx-auto xl:max-w-8xl 2xl:max-w-screen-2xl' : ''}
            ${showSidebar ? 'lg:flex lg:flex-col lg:justify-start lg:items-stretch' : ''}
            transition-all duration-500 ease-luxury
          `}>

            {/* Wallet Connection Check */}
            {!isWalletConnected ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                <div className="bg-current/5 rounded-lg p-8 max-w-md">
                  <h2 className="text-xl font-semibold mb-4">Wallet Not Connected</h2>
                  <p className="text-sm opacity-70 mb-6">
                    Please connect your wallet to access the Etherith Memory Vault
                  </p>
                  <div className="text-xs opacity-50">
                    Connect your wallet using the button in the top navigation
                  </div>
                </div>
              </div>
            ) : (
              /* Page Content with Animations */
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentView}
                  initial={shouldAnimate ? "initial" : "in"}
                  animate="in"
                  exit={shouldAnimate ? "out" : "in"}
                  variants={pageVariants}
                  transition={shouldAnimate ? pageTransition : { duration: 0 }}
                  className="h-full"
                >
                <ViewComponent
                  memories={currentView === 'archive' ? combinedPublicMemories : vaultMemories}
                  user={user}
                  isAuthenticated={isAuthenticated}
                  onUpload={uploadMemory}
                  onSearch={searchArchive}
                  onRefresh={currentView === 'archive' ? loadPublicArchive : () => {
                    loadMemories()
                    loadPublicArchive() // Also refresh public memories for vault view
                  }}
                  onNavigate={switchView}
                  wallet={wallet}
                  isArchiveMode={isArchiveMode}
                />
              </motion.div>
            </AnimatePresence>
            )}


            {/* Debug Panel */}
            <DebugPanel
              debugInfo={debugInfo}
              isArchiveMode={isArchiveMode}
              checkSyncStatus={checkSyncStatus}
              forcePeerDiscovery={forcePeerDiscovery}
              forcePublicArchiveSync={forcePublicArchiveSync}
              peerCount={peerCount}
              networkStatus={networkStatus}
              p2pMemories={p2pMemories}
            />
          </div>
        </main>
      </div>

      {/* Mobile Wallet FAB - Provides prominent wallet access */}
      {isMobile && (
        <WalletFAB
          variant="smart"
          position="bottom-right"
          offset="nav-aware"
        />
      )}

      {/* Wallet Onboarding - Auto-shows for new users */}
      <WalletOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        autoShow={true}
      />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Viewport Debugger - Development only */}
      <ViewportDebugger />
    </div>
  )
}

/**
 * App - Main application component with all providers
 */
function App() {
  return (
    <ErrorBoundary>
      <PublicArchiveProvider>
        <ResponsiveProvider>
          <AppContent />
        </ResponsiveProvider>
      </PublicArchiveProvider>
    </ErrorBoundary>
  )
}

export default App